import * as React from 'react';
import { AuthActionCreators } from '@nkr/store/index';
import { useAppHookContext } from '@nkr/utils/rest-api/useAppHookContext';
import { ITemplate } from './index';

interface IProps {
    templates: ITemplate[];
}

interface IBulkResult {
    templateId: number;
    templateType: string;
    success: boolean;
    message: string;
    group: string;
}

const BulkSendPanel = ({ templates }: IProps) => {
    const { dispatch } = useAppHookContext();
    const [emailAddress, setEmailAddress] = React.useState('nkrtestemail@gmail.com');
    const [organType, setOrganType] = React.useState<0 | 1>(0);
    const [sending, setSending] = React.useState(false);
    const [results, setResults] = React.useState<IBulkResult[]>([]);
    const [summary, setSummary] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const liverCapableIds = templates.filter(t => t.liverCapable).map(t => t.id);
    const liverCount = liverCapableIds.length;
    const sendCount = organType === 1 ? liverCount : templates.length;

    const handleSendAll = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!emailAddress.trim()) return;

        setSending(true);
        setResults([]);
        setSummary(null);
        setError(null);

        try {
            const res: any = await dispatch(AuthActionCreators.apiPost({
                url: __API__ + '/admin/email-test-harness/send-bulk',
                data: { templateTypes: organType === 1 ? liverCapableIds : [], emailAddress: emailAddress.trim(), organType }
            }));

            if (res.data.success) {
                setResults(res.data.results);
                const passed = res.data.results.filter((r: IBulkResult) => r.success).length;
                setSummary(`${passed} / ${res.data.results.length} sent successfully.`);
            } else {
                setError(res.data.message);
            }
        } catch (err: any) {
            setError(err?.response?.data?.message ?? 'Bulk send failed. Check server logs.');
        } finally {
            setSending(false);
        }
    };

    const failCount = results.filter(r => !r.success).length;

    return (
        <form onSubmit={handleSendAll}>
            <div className='row'>
                <div className='col-md-6'>
                    <div className='form-group'>
                        <label>Test Email Address *</label>
                        <input
                            type='email'
                            className='form-control'
                            value={emailAddress}
                            onChange={e => setEmailAddress(e.target.value)}
                            placeholder='nkrtestemail@gmail.com'
                            required
                        />
                    </div>
                </div>
                <div className='col-md-6'>
                    <div className='form-group'>
                        <label>Organ Type</label>
                        <div>
                            <label className='radio-inline'>
                                <input type='radio' checked={organType === 0} onChange={() => setOrganType(0)} />
                                {' '}Kidney
                            </label>
                            {' '}
                            <label className='radio-inline'>
                                <input type='radio' checked={organType === 1} onChange={() => setOrganType(1)} />
                                {' '}Liver
                            </label>
                        </div>
                        {organType === 1 && (
                            <p className='text-muted' style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}>
                                Only the {liverCount} liver-capable templates will be sent. The remaining {templates.length - liverCount} kidney-only templates are skipped.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className='row'>
                <div className='col-md-12' style={{ paddingTop: 10 }}>
                    <button
                        type='submit'
                        className='btn btn-warning'
                        disabled={sending || !emailAddress.trim()}
                    >
                        {sending
                            ? `Sending... (${sendCount} emails, ~${Math.round(sendCount * 0.5)}s)`
                            : `Send All ${sendCount} Emails`}
                    </button>
                </div>
            </div>

            <p className='text-muted' style={{ marginTop: -8 }}>
                Sends {sendCount} templates sequentially with a 500ms delay. This may take about{' '}
                {Math.round(sendCount * 0.5)} seconds.
            </p>

            {error && <div className='alert alert-danger'>{error}</div>}

            {summary && (
                <div className={`alert alert-${failCount === 0 ? 'success' : 'warning'}`}>
                    {summary}
                </div>
            )}

            {results.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <table className='table table-bordered table-condensed' style={{ fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 50 }}>ID</th>
                                <th>Template</th>
                                <th style={{ width: 110 }}>Group</th>
                                <th style={{ width: 70, textAlign: 'center' }}>Status</th>
                                <th>Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map(r => (
                                <tr key={r.templateId} className={r.success ? '' : 'danger'}>
                                    <td>{r.templateId}</td>
                                    <td>{r.templateType}</td>
                                    <td>{r.group}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {r.success
                                            ? <span className='text-success'>&#10003;</span>
                                            : <span className='text-danger'>&#10007;</span>}
                                    </td>
                                    <td>{r.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </form>
    );
};

export default BulkSendPanel;
